import { assign } from '../../utilities/objects';

let actionId = 0;

/**
 * @public
 */
export interface IAction {
  type: string;
}

/**
 * Copied from Redux.
 * @public
 */
export type IThunkAction<R, S, E> = (dispatch: IDispatch<S>, getState: () => S, extraArgument: E) => R;

/**
 * @public
 */
export interface IActionWithPayload<TType extends string, TPayload> extends IAction {
  type: TType;
  payload: TPayload;
}

/**
 * Copied from Redux.
 * @public
 */
export interface IDispatch<S> {
  <R, E>(asyncAction: IThunkAction<R, S, E>): R;
  <A extends IAction>(action: A): A;
}

/**
 * Represents a creator for actions which may be dispatched for handling by
 * store reducers.
 * @public
 */
export interface IActionCreator<TType extends string, TPayload> {
  readonly type: TType;
  readonly actionType: IActionWithPayload<TType, TPayload>;
  readonly payloadType: TPayload;
  (payload: TPayload): IActionWithPayload<TType, TPayload>;
  matches(action: IAction): action is IActionWithPayload<TType, TPayload>;
}

/**
 * Represents a creator for actions
 * @public
 */
export interface IAsyncActionCreator<TPayload, TAction> {
  readonly actionType: TAction;
  readonly payloadType: TPayload;
  (payload: TPayload): TAction;
}

/**
 * Creates an action creator for actions with the specified type.
 * @param type - The type to use for the action.
 * @public
 */
export function createActionCreator<TType extends string, TPayload>(type: TType): IActionCreator<TType, TPayload> {
  const finalType = <TType>`${type}_${++actionId}`;

  return assign((payload: TPayload) => {
    return {
      type: finalType,
      payload: payload
    };
  }, {
      type: finalType,
      // tslint:disable-next-line:no-non-null-assertion
      actionType: undefined!,
      // tslint:disable-next-line:no-non-null-assertion
      payloadType: undefined!,
      matches(action: IAction): action is IActionWithPayload<TType, TPayload> {
        return !!action && action.type === finalType;
      }
    });
}

/**
 * Creates an action creator for actions of a specific
 * @public
 */
export function createAsyncActionCreator<TPayload, TAction>(
  createAction: (payload: TPayload) => TAction
): IAsyncActionCreator<TPayload, TAction> {
  return assign((payload: TPayload) => {
    return createAction(payload);
  }, {
      // tslint:disable-next-line:no-non-null-assertion
      actionType: undefined!,
      // tslint:disable-next-line:no-non-null-assertion
      payloadType: undefined!
    });
}
